import { useCallback, useMemo } from 'react';
import { useReactiveVar } from '@apollo/client';

import { useMyCareerVacancyFeedQuery, useMyCareerFilterDictionariesQuery } from 'core/graphql';
import { authState } from 'core/state';
import { isCareerViewer } from './isCareerViewer';

import { IFeedVacancy, transformFeedVacancy } from './transform';

export interface ICareerFeedFilters {
  directionId?: string;
  skillIds?: string[];
  cityId?: string;
  workMode?: string;
  seniority?: string;
  employmentType?: string;
  salaryFrom?: number;
  freshness?: string;
  search?: string;
  savedOnly?: boolean;
  sort?: string;
}

const PAGE_SIZE = 20;

/** Blanks collapse to `undefined` so the server drops the arg instead of matching on ''. */
const clean = (value?: string): string | undefined => value?.trim() || undefined;

/**
 * The ranked, paged vacancy feed (spec 0035).
 *
 * Replaces `useCareerVacancies` for the list. Three things it does that the
 * old hook could not:
 *
 * * **Ranks against the student's own profile** — and reports `relevanceApplied`
 *   so the page can stop claiming a match when there is nothing to match on;
 * * **Pages with a keyset cursor**, which survives the nightly agent
 *   publishing into the top of the order while someone is scrolling;
 * * **Backs its filters with real dictionaries** instead of scraping direction
 *   options out of the feed's own rows into a ref that never reset.
 *
 * `fetchMore` appends: the merge lives in the Apollo type policy so a
 * page-2 fetch does not replace page 1.
 */
export const useCareerVacancyFeed = (filters: ICareerFeedFilters) => {
  const { active_role, isDemoUser } = useReactiveVar(authState);
  // Every career resolver rejects a non-Student LMS token, so running this for
  // anyone else would only produce a guaranteed PermissionDenied.
  const skip = !isCareerViewer(active_role) || Boolean(isDemoUser);

  const variables = useMemo(
    () => ({
      directionId: clean(filters.directionId),
      skillIds: filters.skillIds?.length ? filters.skillIds : undefined,
      cityId: clean(filters.cityId),
      workMode: clean(filters.workMode),
      seniority: clean(filters.seniority),
      employmentType: clean(filters.employmentType),
      salaryFrom: filters.salaryFrom || undefined,
      freshness: clean(filters.freshness),
      search: clean(filters.search),
      // `undefined`, not `false`: the server then never receives the argument
      // and the cache key matches the plain feed, so toggling the switch back
      // and forth costs no extra request.
      savedOnly: filters.savedOnly ? true : undefined,
      sort: clean(filters.sort),
      first: PAGE_SIZE,
    }),
    [filters],
  );

  const { data, loading, error, fetchMore } = useMyCareerVacancyFeedQuery({
    variables,
    fetchPolicy: 'cache-and-network',
    notifyOnNetworkStatusChange: true,
    skip,
  });

  const connection = data?.careerVacancyFeed;

  const vacancies: IFeedVacancy[] = useMemo(
    () => (connection?.edges ?? []).map((edge) => transformFeedVacancy(edge.node)),
    [connection],
  );

  const loadMore = useCallback(() => {
    const cursor = connection?.pageInfo?.endCursor;
    if (!cursor || !connection?.pageInfo?.hasNextPage) return;
    fetchMore({ variables: { ...variables, after: cursor } });
  }, [connection, fetchMore, variables]);

  return {
    vacancies,
    totalCount: connection?.totalCount ?? 0,
    totalCountCapped: Boolean(connection?.totalCountCapped),
    // 🔴 The page must read this before saying anything about matching.
    relevanceApplied: Boolean(connection?.relevanceApplied),
    hasMore: Boolean(connection?.pageInfo?.hasNextPage),
    loading,
    error: error?.message || '',
    loadMore,
  };
};

/** Direction and city option lists for the filter panel. */
export const useCareerFilterDictionaries = () => {
  const { active_role, isDemoUser } = useReactiveVar(authState);
  const skip = !isCareerViewer(active_role) || Boolean(isDemoUser);

  // `cache-first`: the panel needs its options before any result arrives, and
  // a curated dictionary does not change during a session.
  const { data } = useMyCareerFilterDictionariesQuery({
    fetchPolicy: 'cache-first',
    skip,
  });

  const directionOptions = useMemo(
    () =>
      (data?.careerDirectionsLms ?? []).map((row) => ({
        value: row.id,
        label: row.title,
      })),
    [data],
  );

  const cityOptions = useMemo(
    () =>
      (data?.careerCities ?? []).map((row) => ({
        value: row.id,
        label: row.name,
      })),
    [data],
  );

  return { directionOptions, cityOptions };
};
